import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Tutor } from '../types';
import Constants from 'expo-constants';
import { API_URLS } from '../api/config';

interface TutorContextType {
  selectedTutor: Tutor | null;
  setSelectedTutor: (tutor: Tutor | null) => void;
  tutors: Tutor[];
  fetchTutors: () => Promise<void>;
  error: string | null;
  isLoading: boolean;
  getLanguageName: (code: string) => string;
}

const TutorContext = createContext<TutorContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedTutorId';

const SUPPORTED_LANGUAGES: Record<string, string> = {
  'pt-br': 'Portuguese',
  'en-us': 'English',
  'es-es': 'Spanish',
  'es-mx': 'Spanish',
  'fr-fr': 'French',
  'de-de': 'German',
  'it-it': 'Italian',
  'ru-ru': 'Russian',
};

export const TutorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [selectedTutor, setSelectedTutor] = useState<Tutor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const handleSetSelectedTutor = useCallback((tutor: Tutor | null) => {
    setSelectedTutor(tutor);
    
    if (tutor) {
      try {
        localStorage.setItem(STORAGE_KEY, tutor.id);
      } catch (error) {
        console.error('Error saving tutor selection:', error);
      }
    } else {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('Error removing tutor selection:', error);
      }
    }
  }, []);

  const fetchTutors = useCallback(async () => {
    if (isLoading) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(API_URLS.tutors);
      
      let fetchedTutors: Tutor[] = [];
      if (response.data.tutors) {
        fetchedTutors = response.data.tutors;
      } else if (Array.isArray(response.data)) {
        fetchedTutors = response.data;
      } else {
        throw new Error('Invalid response format');
      }

      setTutors(fetchedTutors);

      // Only set initial tutor if none is selected
      if (!selectedTutor && fetchedTutors.length > 0) {
        const savedTutorId = localStorage.getItem(STORAGE_KEY);
        if (savedTutorId) {
          const savedTutor = fetchedTutors.find((t: Tutor) => t.id === savedTutorId);
          if (savedTutor) {
            handleSetSelectedTutor(savedTutor);
          } else {
            // If saved tutor not found, remove from storage and use first tutor
            localStorage.removeItem(STORAGE_KEY);
            handleSetSelectedTutor(fetchedTutors[0]);
          }
        } else {
          handleSetSelectedTutor(fetchedTutors[0]);
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(`Failed to fetch tutors: ${error.response?.data?.message || error.message}`);
      } else {
        setError('Failed to fetch tutors. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, handleSetSelectedTutor, selectedTutor]);

  const getLanguageName = useCallback((code: string): string => {
    return SUPPORTED_LANGUAGES[code.toLowerCase()] || code.toUpperCase();
  }, []);

  // Fetch tutors on mount
  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchTutors();
      hasFetchedRef.current = true;
    }
  }, [fetchTutors]);

  const value = {
    selectedTutor,
    setSelectedTutor: handleSetSelectedTutor,
    tutors,
    fetchTutors,
    error,
    isLoading,
    getLanguageName,
  };

  return <TutorContext.Provider value={value}>{children}</TutorContext.Provider>;
};

export const useTutor = () => {
  const context = useContext(TutorContext);
  if (context === undefined) {
    throw new Error('useTutor must be used within a TutorProvider');
  }
  return context;
};
